// useInfiniteRows — wiederverwendbares Offset/Limit-Infinite-Scrolling.
// Generalisiert das Muster aus pages/ContactTable.tsx (Initial-Load-Effect +
// fetchMore): AbortController/last-wins, Append + Dedupe per getKey,
// hasMore aus total (X-Total-Count) bzw. Seitenfüllung. `initialOffset` lädt
// beim Wiederherstellen (Detail-Rücksprung) in EINEM Request `limit=initialOffset`.
// `enabled` (Default true) hält das Laden an, bis der Aufrufer bereit ist.

import { type Key, useCallback, useEffect, useRef, useState } from "react";

export const DEFAULT_PAGE_SIZE = 30;

export type FetchPage<T> = (args: {
  limit: number;
  offset: number;
  signal: AbortSignal;
}) => Promise<{ rows: T[]; total: number | null }>;

export type UseInfiniteRows<T> = {
  rows: T[];
  total: number | null;
  hasMore: boolean;
  loading: boolean;
  error: unknown;
  loadMore: () => void;
  reset: () => void;
  /** Entfernt Zeilen lokal (z.B. nach Löschen) ohne Refetch; senkt total. */
  removeByKeys: (keys: Key[]) => void;
  /** Ersetzt EINE Zeile lokal (z.B. Kind-Liste patchen) ohne Refetch. */
  patchByKey: (key: Key, patch: (row: T) => T) => void;
};

export function useInfiniteRows<T>(opts: {
  fetchPage: FetchPage<T>;
  getKey: (row: T) => Key;
  pageSize?: number;
  /** Reset + Neu-Laden, wenn sich diese Werte ändern (Suche/Filter/Sort/Scope). */
  deps: unknown[];
  /** Erste Ladung lädt initialOffset Zeilen (für State-Restore). */
  initialOffset?: number;
  /**
   * `false` = noch nicht laden (weder Erst-Load noch loadMore). Wird es
   * `true`, lädt der Hook EINMAL mit den dann gültigen deps. T-400: die
   * Vorgangsliste wartet so auf die gespeicherten Einstellungen, statt erst
   * mit dem Standard und gleich danach noch einmal zu laden. Default `true`.
   */
  enabled?: boolean;
}): UseInfiniteRows<T> {
  const { fetchPage, getKey, pageSize = DEFAULT_PAGE_SIZE, deps, initialOffset, enabled = true } = opts;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const [rows, setRows] = useState<T[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const rowsRef = useRef<T[]>([]);
  rowsRef.current = rows;
  const loadingRef = useRef(false);
  // In-flight loadMore-Request — muss beim Reset (deps-Änderung/Reload) mit
  // abgebrochen werden, sonst hängt seine (dann stale) Antwort nach dem
  // frischen Erst-Load wieder alte Zeilen an (z. B. eine gerade gelöschte).
  const loadMoreControllerRef = useRef<AbortController | null>(null);
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  const getKeyRef = useRef(getKey);
  getKeyRef.current = getKey;

  const dedupeAppend = useCallback((prev: T[], incoming: T[]): T[] => {
    const seen = new Set(prev.map((r) => getKeyRef.current(r)));
    const merged = [...prev];
    for (const r of incoming) {
      const k = getKeyRef.current(r);
      if (!seen.has(k)) {
        seen.add(k);
        merged.push(r);
      }
    }
    return merged;
  }, []);

  // Initial-Load + Reset bei deps-Änderung (und sobald `enabled` wird).
  useEffect(() => {
    if (!enabled) {
      // Ein beim Abschalten abgebrochener Load hinterließe sonst `loading`.
      setLoading(false);
      loadingRef.current = false;
      return;
    }
    const controller = new AbortController();
    const firstLimit = initialOffset && initialOffset > pageSize ? initialOffset : pageSize;
    setLoading(true);
    loadingRef.current = true;
    setError(null);
    fetchPageRef
      .current({ limit: firstLimit, offset: 0, signal: controller.signal })
      .then(({ rows: incoming, total: t }) => {
        if (controller.signal.aborted) return;
        const seen = new Set<Key>();
        const deduped = incoming.filter((r) => {
          const k = getKeyRef.current(r);
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        setRows(deduped);
        setTotal(t);
        setHasMore(t != null ? deduped.length < t : incoming.length >= firstLimit);
      })
      .catch((e) => {
        if (controller.signal.aborted || (e as Error)?.name === "AbortError") return;
        setError(e);
        setHasMore(false);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        loadingRef.current = false;
      });
    return () => {
      controller.abort();
      // Auch eine laufende Nachlade-Seite stirbt mit — Cleanup läuft bei
      // deps-Änderung VOR dem nächsten Effect-Lauf und beim Unmount.
      loadMoreControllerRef.current?.abort();
      loadMoreControllerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || !enabledRef.current) return;
    const controller = new AbortController();
    loadMoreControllerRef.current = controller;
    const offset = rowsRef.current.length;
    setLoading(true);
    loadingRef.current = true;
    fetchPageRef
      .current({ limit: pageSize, offset, signal: controller.signal })
      .then(({ rows: incoming, total: t }) => {
        if (controller.signal.aborted) return;
        const next = dedupeAppend(rowsRef.current, incoming);
        setRows(next);
        if (t != null) setTotal(t);
        setHasMore(t != null ? next.length < t : incoming.length >= pageSize);
      })
      .catch((e) => {
        if (controller.signal.aborted || (e as Error)?.name === "AbortError") return;
        setError(e);
        setHasMore(false);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        loadingRef.current = false;
      });
  }, [dedupeAppend, pageSize]);

  const reset = useCallback(() => {
    setRows([]);
    setTotal(null);
    setHasMore(true);
  }, []);

  const removeByKeys = useCallback((keys: Key[]) => {
    if (keys.length === 0) return;
    const kill = new Set(keys);
    setRows((prev) => prev.filter((r) => !kill.has(getKeyRef.current(r))));
    setTotal((t) => (t == null ? t : Math.max(0, t - keys.length)));
  }, []);

  const patchByKey = useCallback((key: Key, patch: (row: T) => T) => {
    setRows((prev) => prev.map((r) => (getKeyRef.current(r) === key ? patch(r) : r)));
  }, []);

  return { rows, total, hasMore, loading, error, loadMore, reset, removeByKeys, patchByKey };
}
